/**
 * Shared commenter-identity helpers: a deterministic hash, a stable
 * "commenter_xxxx" handle, and a generated "fingerprint" identicon avatar.
 *
 * Same commenter id → same color, handle, and pattern across every screen
 * (Moderation Dashboard and Review Queue), so anonymous/restricted Facebook
 * profiles stay visually distinguishable instead of collapsing into one
 * identical gray icon.
 */
import React, { useState } from "react";

// Deterministic 32-bit FNV-1a hash → stable per-commenter color/handle/pattern.
export const hashString = (str: string): number => {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

// Short stable suffix for anonymous handles, e.g. "commenter_3f9a".
export const shortId = (seed: string): string =>
  hashString(seed).toString(36).slice(0, 4).padStart(4, "0");

// Names Facebook returns when the commenter's real name is restricted by
// privacy settings (or legacy rows that hardcoded a placeholder). We treat
// these as "no name" so the commenter_xxxx handle kicks in instead.
const PLACEHOLDER_NAMES = new Set(["facebook user", "instagram user", "unknown user", "anonymous", ""]);

export interface CommenterIdentity {
  name: string;
  picture: string | null;
  isAnonymous: boolean;
  seed: string;
}

// Resolve a stable display identity from a comment-like object. Accepts the
// loose shapes used across the app (from / user / author, etc.).
export const getCommenterIdentity = (comment: any): CommenterIdentity => {
  const from = comment?.from || comment?.user || comment?.author || {};
  const rawName =
    from.name ||
    from.displayName ||
    from.username ||
    comment?.userName ||
    comment?.authorName ||
    null;
  const trimmed = rawName ? String(rawName).trim() : "";
  const resolvedName =
    trimmed && !PLACEHOLDER_NAMES.has(trimmed.toLowerCase()) ? trimmed : null;
  const picture = from.picture || from.avatar || from.profilePicture || null;
  // Stable seed for the handle + identicon: prefer the commenter's FB id
  // (ASID, stable per person+page); fall back to the comment id so each row
  // is at least internally consistent.
  const seed = String(
    from.id ||
      comment?.fromId ||
      comment?.commentId ||
      comment?.id ||
      resolvedName ||
      "anon"
  );

  return {
    name: resolvedName || `commenter_${shortId(seed)}`,
    picture,
    isAnonymous: !resolvedName,
    seed,
  };
};

// Generated "fingerprint" avatar: a 5×5 horizontally-mirrored block identicon
// deterministically seeded by the commenter id — a unique gradient-tinted
// pattern per commenter. Renders the real picture when available, falling back
// to the identicon if the picture is missing or fails to load.
export const CommentAvatar: React.FC<{
  info: { name: string; picture: string | null; seed: string };
  size?: number;
}> = ({ info, size = 32 }) => {
  const [imgError, setImgError] = useState(false);

  if (info.picture && !imgError) {
    return (
      <img
        src={info.picture}
        alt={info.name}
        width={size}
        height={size}
        style={{ width: size, height: size }}
        className="rounded-full border object-cover flex-shrink-0"
        onError={() => setImgError(true)}
      />
    );
  }

  const h = hashString(info.seed);
  const hue = h % 360;
  const fg = `hsl(${hue}, 58%, 48%)`;
  const bg = `hsl(${hue}, 58%, 94%)`;
  const cell = size / 5;
  const rects: JSX.Element[] = [];
  // 3 left columns drive the pattern; mirror to the right 2 for symmetry.
  for (let x = 0; x < 3; x++) {
    for (let y = 0; y < 5; y++) {
      if ((h >> (x * 5 + y)) & 1) {
        rects.push(
          <rect key={`${x}-${y}`} x={x * cell} y={y * cell} width={cell} height={cell} />
        );
        if (x < 2) {
          rects.push(
            <rect key={`m-${x}-${y}`} x={(4 - x) * cell} y={y * cell} width={cell} height={cell} />
          );
        }
      }
    }
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="rounded-full border flex-shrink-0"
      style={{ backgroundColor: bg }}
      role="img"
      aria-label={info.name}
    >
      <g fill={fg}>{rects}</g>
    </svg>
  );
};
